import { ADMIN_NAV } from '@/data/admin-nav';

/**
 * Mappage navItem (clé `k`) → permissions autorisant l'accès (OU logique).
 * Source unique de vérité partagée par la sidebar (affichage) et les gardes de
 * route (accès). Si une clé n'est pas listée, l'entrée est ouverte à tout
 * utilisateur authentifié.
 */
export const ITEM_PERMISSIONS: Record<string, string[]> = {
  reporting: ['REPORTING.DIRECTION.READ', 'FINANCE.READ', 'LOGISTICS.READ', 'SUPPORT.READ'],
  orders: ['ORDERS.READ'],
  'orders-search': ['ORDERS.READ'],
  'orders-stats': ['ORDERS.READ'],
  returns: ['ORDERS.RETURNS.READ'],
  catalog: ['CATALOG.READ'],
  categories: ['CATALOG.READ'],
  customers: ['CUSTOMERS.READ'],
  logistics: ['LOGISTICS.READ'],
  'delivery-zones': ['LOGISTICS.READ'],
  suppliers: ['SUPPLIERS.READ'],
  support: ['SUPPORT.READ'],
  quotes: ['QUOTES.READ'],
  payments: ['PAYMENT.READ', 'FINANCE.READ'],
  finance: [
    'FINANCE.READ',
    'FINANCE.CREDIT_NOTE.ISSUE',
    'FINANCE.EXPORT',
    'FINANCE.EXPORT.ACCOUNTING',
    'FINANCE.STATEMENTS',
    'FINANCE.BUDGET.EDIT',
    'FINANCE.AUDIT',
  ],
  'fin-invoices': ['FINANCE.READ'],
  'fin-credit-notes': ['FINANCE.READ', 'FINANCE.CREDIT_NOTE.ISSUE'],
  'fin-journals': ['FINANCE.READ'],
  'fin-chart': ['FINANCE.READ'],
  'fin-vat': ['FINANCE.READ'],
  'fin-exports': ['FINANCE.EXPORT', 'FINANCE.EXPORT.ACCOUNTING'],
  'fin-statements': ['FINANCE.STATEMENTS', 'FINANCE.READ'],
  'fin-budgets': ['FINANCE.BUDGET.EDIT', 'FINANCE.READ'],
  'fin-audit': ['FINANCE.AUDIT'],
  /**
   * « Marketing & Contenus » regroupe dix-sept écrans aux exigences
   * différentes : l'entrée de barre latérale ouvre dès qu'UNE d'elles est
   * satisfaite, et chaque écran reste gardé pour son compte — par sa route
   * (voir routes/index.tsx) et par la colonne du module, qui masque ce qui
   * n'est pas accessible et bloque aussi l'accès direct par l'URL.
   */
  marketing: [
    'MARKETING.CMS.READ',
    'MARKETING.CMS.WRITE',
    'MARKETING.CAMPAIGN.SEND',
    'SYSTEM.SETTINGS.WRITE',
    'CATALOG.READ',
  ],
  admin: ['USERS.READ'],
  hubs: ['LOGISTICS.READ', 'CATALOG.READ'],
};

/**
 * Première route du périmètre de l'utilisateur, en suivant l'ordre de la
 * navigation. Sert de cible de repli quand l'accueil consolidé n'est pas
 * accessible (profil métier sans FINANCE.READ).
 *
 * On ne retient que les pages explicitement gardées par une permission
 * satisfaite : les pages « ouvertes à tout authentifié » (sans exigence, ex.
 * marques/attributs) ne sont pas un point d'atterrissage pertinent pour un
 * agent métier. `/profil` (toujours ouvert) sert d'ultime repli.
 */
export function firstAuthorizedPath(hasAny: (...c: string[]) => boolean): string {
  for (const group of ADMIN_NAV) {
    for (const item of group.items) {
      const required = ITEM_PERMISSIONS[item.k];
      if (!required) continue;
      if (hasAny(...required)) return item.path;
    }
  }
  return '/profil';
}
